import { Menu } from "lucide-react";
import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import GradientText from "@/components/shared/GradientText";
import { ROUTES } from "@/constants/routes";
import { useResponsiveNav, usePreloadOnHover } from "@/hooks";
import { getFocusedElement } from "@/utils/accessibility";
import MoreDropdown from "@/components/layout/MoreDropdown";
import React from "react";

interface HeaderProps {
  isVisible?: boolean;
}

export default function Header({ isVisible = true }: HeaderProps = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { visibleItems, moreItems, containerRef } = useResponsiveNav();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const isActiveRoute = (route: string) => {
    return location.pathname === route;
  };

  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
    const isActive = isActiveRoute(to);
    const baseClasses =
      "font-sans font-normal text-[16px] min-[481px]:text-[18px] min-[2200px]:text-[20px] tracking-[-0.18px] hover:opacity-80 transition-opacity whitespace-nowrap flex-shrink-0 relative pb-1";
    const preloadProps = usePreloadOnHover(to);

    return (
      <Link
        to={to}
        className={`${baseClasses} ${isActive ? "border-b-[1px] border-[#C0C0C0]" : ""}`}
        aria-current={isActive ? "page" : undefined}
        {...preloadProps}
      >
        {isActive ? (
          <GradientText className="text-[16px] min-[481px]:text-[18px] min-[2200px]:text-[20px] tracking-[-0.18px]">
            {children}
          </GradientText>
        ) : (
          <span className="text-white">{children}</span>
        )}
      </Link>
    );
  };

  const MobileNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
    const isActive = isActiveRoute(to);
    const baseClasses =
      "font-sans font-normal text-[16px] tracking-[-0.18px] min-h-[44px] flex items-center relative pb-1";

    return (
      <Link
        to={to}
        className={`${baseClasses} ${isActive ? "border-b-[1px] border-[#C0C0C0]" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-current={isActive ? "page" : undefined}
      >
        {isActive ? (
          <GradientText className="text-[16px] tracking-[-0.18px]">
            {children}
          </GradientText>
        ) : (
          <span className="text-white">{children}</span>
        )}
      </Link>
    );
  };

  return (
    <header
      role="banner"
      className={`fixed top-0 z-100 w-full ${
        isVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        ref={containerRef}
        className="backdrop-blur-[25px] backdrop-filter bg-[rgba(19,19,19,0.7)] relative w-full min-h-[72px] min-[481px]:h-[96px] min-[2200px]:h-[108px] transition-[opacity,transform,background-color] duration-300 ease-in-out border-b border-[rgba(255,255,255,0.12)]"
        style={{
          willChange: "transform",
          transform: "translateZ(0)",
        }}
      >
        <div className="relative flex items-center justify-between px-4 max-md:px-4 min-[481px]:px-6 2xl:px-8 min-[2200px]:px-12 h-full py-4">
          {/* Logo */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center z-10 flex-shrink-0 max-[360px]:pr-1 max-[400px]:pr-2 pr-8 2xl:pr-12 min-[2200px]:pr-16 pb-1"
            onClick={() => setMobileMenuOpen(false)}
          >
            <div className="max-[360px]:max-w-[160px] max-[400px]:max-w-[200px] max-[480px]:max-w-[240px]">
              <Logo
                gradientId="paint0_radial_header"
                variant="text"
                maxWidth={320}
                className="w-full h-auto"
                gradient={false}
              />
            </div>
          </Link>

          {/* Desktop Menu */}
          <nav
            id="main-navigation"
            role="navigation"
            aria-label="Main navigation"
            className="hidden min-[768px]:flex items-center flex-nowrap gap-6 2xl:gap-8 min-[2200px]:gap-10 flex-1 justify-center whitespace-nowrap px-4 2xl:px-6 min-[2200px]:px-8"
          >
            {visibleItems.map((item) => (
              <NavLink key={item.route} to={item.route}>
                {item.label}
              </NavLink>
            ))}
            <MoreDropdown moreItems={moreItems} />
          </nav>

          {/* CTA Button - Desktop */}
          <Link
            to={ROUTES.WHITEPAPER}
            className="hidden min-[768px]:block bg-white px-4 py-2 2xl:px-6 2xl:py-3 min-[2200px]:px-8 min-[2200px]:py-4 rounded-[100px] hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#09091e] flex-shrink-0 ml-4 2xl:ml-6 min-[2200px]:ml-8"
            aria-label="Whitepaper"
          >
            <p className="font-['Suisse_Intl:Book',_sans-serif] leading-[20px] 2xl:leading-[24px] min-[2200px]:leading-[28px] text-[16px] 2xl:text-[20px] min-[2200px]:text-[22px] text-[rgba(7,7,8,0.9)] text-nowrap tracking-[-0.2px] whitespace-pre">
              Whitepaper
            </p>
          </Link>

          {/* Mobile Menu Button */}
          <button
            ref={menuButtonRef}
            className="max-[767px]:flex min-[768px]:hidden text-white min-w-[44px] min-h-[44px] items-center justify-center -mr-2"
            onClick={() => {
              if (!mobileMenuOpen) {
                previousFocusRef.current = getFocusedElement();
              }
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          ref={mobileMenuRef}
          id="mobile-navigation"
          className={`max-[767px]:block min-[768px]:hidden border-t border-[rgba(255,255,255,0.12)] overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out bg-[rgba(19,19,19,0.7)] backdrop-blur-[25px] ${
            mobileMenuOpen ? "max-h-[calc(100vh-72px)] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <nav
            role="navigation"
            aria-label="Mobile navigation"
            className="flex flex-col gap-3 px-4 overflow-y-auto max-h-[calc(100vh-72px)] py-4"
          >
            {/* Main Navigation Links */}
            <MobileNavLink to={ROUTES.HOME}>Home</MobileNavLink>
            <MobileNavLink to={ROUTES.ABOUT_US}>About Us</MobileNavLink>
            <MobileNavLink to={ROUTES.ECOSYSTEM}>Ecosystem</MobileNavLink>
            <MobileNavLink to={ROUTES.AI_ENGINE}>AI Engine</MobileNavLink>
            <MobileNavLink to={ROUTES.TOKENOMICS}>Tokenomics</MobileNavLink>
            <MobileNavLink to={ROUTES.ELEVATE_TO_ALPHA}>Elevate to Alpha</MobileNavLink>
            <MobileNavLink to={ROUTES.TRACTION}>Traction</MobileNavLink>
            <MobileNavLink to={ROUTES.ROADMAP}>Roadmap</MobileNavLink>
            <MobileNavLink to={ROUTES.USER_JOURNEYS}>User Journeys</MobileNavLink>
            <MobileNavLink to={ROUTES.OUR_TEAM}>Our Team</MobileNavLink>
            <MobileNavLink to={ROUTES.REGULATORY_COMPLIANCE}>
              Regulatory Compliance
            </MobileNavLink>
            <MobileNavLink to={ROUTES.SOCIAL_IMPACT_COLLECTIVE}>
              Social Impact Collective
            </MobileNavLink>

            {/* CTA Button */}
            <Link
              to={ROUTES.WHITEPAPER}
              onClick={() => setMobileMenuOpen(false)}
              className="relative px-4 py-2.5 rounded-full text-white bg-transparent overflow-hidden border-none cursor-pointer transition-opacity hover:opacity-90 active:opacity-80 mt-3 w-full min-h-[44px] flex items-center justify-center"
            >
              <span
                className="relative z-10 font-sans font-medium text-[16px] tracking-[-0.2px] whitespace-nowrap"
                style={{ lineHeight: "1.2" }}
              >
                View Whitepaper
              </span>
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle at top right, #FFE76E 0%, #E926FF 50%, #232EFF 100%)",
                  mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  maskComposite: "exclude",
                  WebkitMask:
                    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  padding: "2px",
                }}
              />
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
}
